/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import './specificFields.tpl.html';

(function () {
  /**
   * This directive displays the controls for configuring the specific fields for an object restore.
   */
  angular
    .module('bulkRestore.specificFields', [])
    .directive('sbsfBulkRestoreSpecificFields', directive)
    .controller('BulkRestoreSpecificFieldsCtrl', controller);

  /**
   * Defines the sbsf-bulk-restore-specific-fields directive.
   *
   * @returns {Object} - The directive settings
   */
  function directive() {
    return {
      controller: 'BulkRestoreSpecificFieldsCtrl',
      templateUrl: 'bulkRestore/settings/specificFields.tpl.html',
      restrict: 'E',
      scope: {}
    };
  }

  controller.$inject = ['$filter', '$q', '$scope', '$state', 'BulkRestoreService', 'ErrorService'];

  /**
   * The controller for the directive.
   */
  function controller($filter, $q, $scope, $state, BulkRestoreService, ErrorService) {
    if (!$state.params.bulkRestoreData) {
      throw new Error('bulkRestoreData is required');
    }

    var bulkRestoreData = $state.params.bulkRestoreData;
    var LOADING = $filter('i18n')('LOADING_MSG_WITH_DOTS');
    var model;

    // Published functions
    $scope.addEntry = addEntry;
    $scope.autoSelectField = autoSelectField;
    $scope.autoSelectObjectType = autoSelectObjectType;
    $scope.calculateSelectedObjectTypes = calculateSelectedObjectTypes;
    $scope.canAddEntry = canAddEntry;
    $scope.canDeleteEntry = canDeleteEntry;
    $scope.deleteEntry = deleteEntry;
    $scope.entriesAreValid = entriesAreValid;
    $scope.hasEntries = hasEntries;
    $scope.hasObjectTypes = hasObjectTypes;
    $scope.objectTypeSelected = objectTypeSelected;
    $scope.toggleField = toggleField;
    $scope.watchSpecifyingFields = watchSpecifyingFields;

    $scope.bulkRestoreData = bulkRestoreData;

    // Store data in a model object rather than the scope itself for easier watching
    $scope.model = model = {
      allObjectTypes: [LOADING],
      selectedObjectTypes: []
    };

    // Get the selected object types and sort
    BulkRestoreService.getSelectedObjectTypesWithUpdateableFields(bulkRestoreData)
      .then(function (types) {
        model.allObjectTypes = _.sortBy(types, function (type) {
          return type.toLowerCase();
        });

        calculateSelectedObjectTypes();

        // Watch for this section to be shown
        watchSpecifyingFields();
      })
      .catch((err) => {
        ErrorService.report(err);
      });

    /**
     * Function to add a new entry to specific field entries array.
     *
     * @returns {Promise} a promise that resolves to the new field.
     */
    function addEntry() {
      var newEntry = bulkRestoreData.addSpecificFieldEntry();
      return $scope.autoSelectObjectType().then(function () {
        return newEntry;
      });
    }

    /**
     * Automatically selects a field for an entry if there is only one unselected field remaining for that
     * object type.
     *
     * @param {object} sfEntry - the specific field entry
     */
    function autoSelectField(sfEntry) {
      // If there's only one field select that one
      if (sfEntry.allValues.length === 1) {
        var firstValue = _.first(sfEntry.allValues);
        $scope.toggleField(sfEntry, firstValue);
        sfEntry.selectedValues.push(firstValue);
      }
    }

    /**
     * Automatically selects an object type if there is only one available type remaining.
     *
     * @returns {Promise} a promise that resolves when the work is done.
     */
    function autoSelectObjectType() {
      // If there's only one remaining unselected type, set that one
      var allCount = model.allObjectTypes.length;
      var selCount = model.selectedObjectTypes.length;
      if (allCount - selCount === 1) {
        var lastEntry = _.last(bulkRestoreData.getSpecificFieldEntries());
        var lastObjectType = _.difference(model.allObjectTypes, model.selectedObjectTypes)[0];
        return $scope.objectTypeSelected(lastEntry, lastObjectType);
      } else {
        return $q.when();
      }
    }

    /**
     * Creates a list of selected object types by getting the object types from the defined specific field entries.
     */
    function calculateSelectedObjectTypes() {
      model.selectedObjectTypes = _.pluck(bulkRestoreData.getSpecificFieldEntries(), 'objectType');
    }

    /**
     * Determines whether a new entry/row can be added.
     *
     * @returns {boolean} true if a new entry/row can be added.
     */
    function canAddEntry() {
      // We can add a new entry if there are more object types available than have been used.
      return model.allObjectTypes.length > bulkRestoreData.getSpecificFieldEntryCount();
    }

    /**
     * Determines whether any entry/row can be deleted. We don't allow the last one to be deleted.
     *
     * @returns {boolean} true if any entry/row can be deleted.
     */
    function canDeleteEntry() {
      return bulkRestoreData.getSpecificFieldEntryCount() > 1;
    }

    /**
     * Deletes a specific field entry/row.
     *
     * @param {object} sfEntry - the entry to be deleted
     */
    function deleteEntry(sfEntry) {
      bulkRestoreData.removeSpecificFieldEntry(sfEntry.objectType);
      $scope.calculateSelectedObjectTypes();
    }

    /**
     * Determines whether the entries are valid. We disable the add button if not.
     *
     * @returns {boolean} - true if the entries are valid.
     */
    function entriesAreValid() {
      return bulkRestoreData.specificFieldsAreValid();
    }

    /**
     * Determines whether there are any object types.
     *
     * @returns {boolean} true if there are object types.
     */
    function hasObjectTypes() {
      return model.allObjectTypes.length > 0;
    }

    /**
     * Called when a new object type is selected in a specific field entry.
     *
     * @param {object} sfEntry - The specific field entry.
     * @param {string} objectType - The object type to select.
     *
     * @returns {Promise} a promise that resolves when all the work is done.
     */
    function objectTypeSelected(sfEntry, objectType) {
      sfEntry.objectType = objectType;
      sfEntry.objectFields = [];
      sfEntry.allValues = [LOADING];
      sfEntry.selectedValues = [];

      return BulkRestoreService.getFieldsForObjectTypes([objectType]).then(function (result) {
        // Only allow updateable fields
        let fields = result[objectType];
        fields = _.filter(fields, function (field) {
          return field.updateable;
        });

        // Sort the field list by name, ignoring case
        sfEntry.allValues = _.sortBy(fields, function (field) {
          return field.name.toLowerCase();
        });

        calculateSelectedObjectTypes();
        autoSelectField(sfEntry);
      });
    }

    /**
     * Utility method to check if we have specific fields.
     * This is needed since we will disable the inputs if we have valid overwrite values but
     * the overwrite values checkbox is unchecked.
     *
     * @returns {boolean} true if we have overwrite values.
     */
    function hasEntries() {
      return bulkRestoreData.getSpecificFieldEntryCount() > 0;
    }

    /**
     * Utility to check or uncheck a field for the current object type.
     *
     * @param {object} sfEntry - The specific field entry
     * @param {object} field - The name of the field to toggle.
     */
    function toggleField(sfEntry, field) {
      // 'field' is the full record. 'objectFields' is just the names.
      var found = _.find(sfEntry.objectFields, function (objectField) {
        return objectField === field.name;
      });

      if (found) {
        sfEntry.objectFields = _.without(sfEntry.objectFields, field.name);
      } else {
        sfEntry.objectFields.push(field.name);
        sfEntry.objectFields = _.sortBy(sfEntry.objectFields, function (fieldName) {
          return fieldName.toLowerCase();
        });
      }
    }

    /**
     * Watches for changes to the setting that determines whether this section is shown.
     */
    function watchSpecifyingFields() {
      $scope.$watch(
        function () {
          return bulkRestoreData.isSpecifyingFields();
        },
        function (newValue) {
          // If the list is empty add a default row
          if (newValue && !hasEntries()) {
            return addEntry();
          }
        }
      );
    }
  }
})();
